import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { listTaskAgendaView } from 'services/TaskServices';
import { queryKeys } from '../../../config/queryClient';
import { LAYOUT } from '../../../utils/constants';

/**
 * Infinite query hook for Agenda View - uses Task Agenda View API
 *
 * This hook manages ALL pages for a column in one infinite query cache.
 * Uses the new Agenda View API endpoint: /task/v2/list-task-agenda-view
 *
 * Note: Initial data should be loaded via useInitialAgendaLoad to prevent multiple API calls.
 * This hook is used for pagination (fetchNextPage) and when filters change.
 *
 * @param {string} columnId - The column/stage ID
 * @param {Object} filters - Filter parameters (searchKeyword, practiceArea, assignedTo, sortType, dueDateFilter, priority, case_id)
 * @returns {Object} - Query result with data, fetchNextPage, hasNextPage, etc.
 */
export const useAgendaColumnInfiniteQuery = (columnId = '', filters = {}, methodType = 'POST', currentView = 'agenda') => {
  const queryClient = useQueryClient();

  // Check if initial data exists in cache (from useInitialAgendaLoad)
  const queryKey = ['agendaTasks', 'column', columnId, filters];
  const cachedData = queryClient.getQueryData(queryKey);
  const hasInitialData = !!cachedData;

  // Check if initial load is in progress
  // Use getQueriesData to find any query that starts with ['agendaTasks', 'initial-load']
  const initialLoadQueries = queryClient.getQueriesData({ queryKey: ['agendaTasks', 'initial-load'] });
  const isInitialLoadInProgress = initialLoadQueries.some(([key, data]) => {
    const state = queryClient.getQueryState(key);
    return state?.status === 'loading' || state?.fetchStatus === 'fetching';
  });

  // Check if this specific query is already fetching to prevent duplicate calls
  const currentQueryState = queryClient.getQueryState(queryKey);
  const isQueryFetching = currentQueryState?.fetchStatus === 'fetching';
  // Check if query is stale (was invalidated) - if stale, we should refetch even if we have data
  // When invalidateQueries is called, isStale becomes true
  // If query state doesn't exist yet, it's a new query and should fetch
  const isQueryStale = currentQueryState?.isStale === true;
  const isNewQuery = !currentQueryState;

  const infiniteQuery = useInfiniteQuery({
    queryKey,

    queryFn: async ({ pageParam = 1 }) => {
      // Only fetch page 2+ (page 1 should be loaded by useInitialAgendaLoad)
      // If pageParam is 1 and we don't have cached data, it means initial load hasn't completed yet
      // In that case, we should still fetch to avoid blocking
      const limit = 5;

      // Build request body per Task Agenda View API documentation
      const requestData = {};

      // Add filters to body (per API documentation)
      if (filters.searchKeyword) {
        requestData.search_keyword = filters.searchKeyword;
      }
      if (filters.practiceArea) {
        requestData.practice_area = filters.practiceArea;
      }
      if (filters.assignedTo && filters.assignedTo.length > 0) {
        // API expects firm_user_ids array
        requestData.firm_user_ids = filters.assignedTo;
      }
      if (filters.contactIds && filters.contactIds.length > 0) {
        // API expects contact_ids array
        requestData.contact_ids = filters.contactIds;
      }
      if (filters.sortType) {
        requestData.sort_type = filters.sortType;
      }
      if (filters.priority) {
        requestData.priority = filters.priority;
      }
      if (filters.dateFilter) {
        requestData.due_date_filter = filters.dateFilter;
      }

      // Call Agenda View API
      // Pass columnId as task_stage_id to fetch only that stage's paginated data
      // This ensures correct pagination for the specific column

      const response = await listTaskAgendaView({
        method: methodType,
        task_stage_id: pageParam === 1 ? '' : columnId,
        limit,
        page: pageParam,
        data: requestData,
        case_id: filters.case_id || null,
      });

      // API returns different structures:
      // 1. When task_stage_id is provided: { data: { task_stage_name, task_count, page, has_more, data: [...] } }
      // 2. When task_stage_id is empty: { data: { "<task_stage_id>": { task_stage_name, task_count, page, has_more, data: [...] } } }
      // Extract data for this specific column
      let columnData = {};
      if (columnId && response?.data?.[columnId]) {
        // Nested structure (when task_stage_id was empty in initial load)
        columnData = response.data[columnId];
      } else if (response?.data && !response?.data?.[columnId]) {
        // Direct structure (when task_stage_id was provided)
        columnData = response.data;
      } else {
        // Fallback
        columnData = response?.data || {};
      }
      const rawItems = columnData?.data || [];

      // Map API response fields to match component expectations
      // API uses: title, status, sub_tasks, assign_to
      // Component expects: task_title, task_status, sub_task, assign_to
      const mappedItems = rawItems.map((item) => {
        // Map subtasks: API returns sub_tasks array, component expects sub_task
        // Also ensure sub_task_status is boolean (API might return string)
        const mappedSubtasks = (item.sub_tasks || item.sub_task || []).map((subtask) => ({
          ...subtask,
          sub_task_status:
            subtask.sub_task_status === true || subtask.sub_task_status === 'true' || subtask.sub_task_status === 'completed',
          sub_task_description: subtask.sub_task_description || subtask.description || '',
        }));

        // Map assignees: API returns objects with firm_user_id, first_name, etc.
        // Component expects assignee_id, assignee_name, first_name, firm_user_id
        const mappedAssignees = (Array.isArray(item.assign_to) ? item.assign_to : []).map((assignee) => ({
          ...assignee,
          assignee_id: assignee.assignee_id || assignee.firm_user_id || assignee.contact_id,
          assignee_name: assignee.assignee_name || assignee.first_name || assignee.full_name || '',
          first_name: assignee.first_name || assignee.full_name?.split(' ')[0] || '',
          firm_user_id: assignee.firm_user_id,
        }));

        // Extract recent_comment safely - API might return string or object
        let recentCommentText = '';
        if (typeof item.recent_comment === 'string') {
          recentCommentText = item.recent_comment;
        } else if (item.recent_comment && typeof item.recent_comment === 'object') {
          recentCommentText = item.recent_comment.comment || '';
        }

        return {
          ...item,
          // Map field names to match existing component structure
          task_title: item.title || item.task_title,
          task_name: item.title || item.task_name,
          task_status: item.status || item.task_status,
          sub_task: mappedSubtasks,
          // Map assignees to expected structure
          assign_to: mappedAssignees,
          // Map recent_comment as a string (not an object)
          recent_comment: recentCommentText,
          // Map case/lead names
          case_name: item.case_name || '',
          lead_name: item.lead_name || '',
          // Ensure priority is properly formatted
          priority: item.priority || '',
          // Preserve task_id and other important fields
          task_id: item.task_id,
          case_id: item.case_id,
          lead_id: item.lead_id,
          due_date: item.due_date,
          description: item.description,
          created_at: item.created_at,
          updated_at: item.updated_at,
        };
      });

      // Map API response to expected format
      // Ensure page number is correctly set from API response or use pageParam as fallback
      const currentPage = columnData?.page !== undefined ? columnData.page : pageParam;
      return {
        items: mappedItems,
        page: currentPage,
        hasMore: columnData?.has_more !== undefined ? columnData.has_more : false,
        totalCount: columnData?.task_count || 0,
        task_stage_name: columnData?.task_stage_name,
      };
    },

    // Get the next page parameter
    getNextPageParam: (lastPage, allPages) => {
      // If no more pages, return undefined to stop pagination
      if (!lastPage.hasMore) return undefined;
      // Return the next page number (current page + 1)
      const nextPage = lastPage.page + 1;

      return nextPage;
    },

    // Get the previous page parameter (optional, for bi-directional scroll)
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page <= 1) return undefined;
      return firstPage.page - 1;
    },

    // Use cached data as initial data if available (from useInitialAgendaLoad)
    initialData: cachedData,

    // Only enable if columnId is provided AND initial load is not in progress
    // This prevents multiple API calls - wait for initial load to complete first
    enabled: !!columnId && !isInitialLoadInProgress && currentView === 'agenda',

    // Refetch on mount if:
    // 1. We don't have initial data, OR
    // 2. Query is stale (was invalidated), OR
    // 3. It's a new query (no state exists yet)
    // AND query is not already fetching (prevents duplicate calls)
    // This ensures API is called when switching views even if cached data exists
    refetchOnMount: (!hasInitialData || isQueryStale || isNewQuery) && !isInitialLoadInProgress && !isQueryFetching,
    refetchOnWindowFocus: false,

    notifyOnChangeProps: 'all',
  });

  // Function to fetch a specific page directly (for direct page navigation)
  // forceRefetch: if true, bypasses cache and always makes API call
  const fetchPage = async (pageNumber, forceRefetch = false) => {
    const queryKey = ['agendaTasks', 'column', columnId, filters];
    const queryFn = async ({ pageParam = pageNumber }) => {
      const limit = 5;

      // Build request body per Task Agenda View API documentation
      const requestData = {};

      // Add filters to body (per API documentation)
      if (filters.searchKeyword) {
        requestData.search_keyword = filters.searchKeyword;
      }
      if (filters.practiceArea) {
        requestData.practice_area = filters.practiceArea;
      }
      if (filters.assignedTo && filters.assignedTo.length > 0) {
        requestData.firm_user_ids = filters.assignedTo;
      }
      if (filters.contactIds && filters.contactIds.length > 0) {
        requestData.contact_ids = filters.contactIds;
      }
      if (filters.sortType) {
        requestData.sort_type = filters.sortType;
      }
      if (filters.priority) {
        requestData.priority = filters.priority;
      }
      if (filters.dateFilter) {
        requestData.due_date_filter = filters.dateFilter;
      }

      const response = await listTaskAgendaView({
        method: methodType,
        task_stage_id: pageParam === 1 ? '' : columnId,
        limit,
        page: pageParam,
        data: requestData,
        case_id: filters.case_id || null,
      });

      let columnData = {};
      if (columnId && response?.data?.[columnId]) {
        columnData = response.data[columnId];
      } else if (response?.data && !response?.data?.[columnId]) {
        columnData = response.data;
      } else {
        columnData = response?.data || {};
      }
      const rawItems = columnData?.data || [];

      const mappedItems = rawItems.map((item) => {
        const mappedSubtasks = (item.sub_tasks || item.sub_task || []).map((subtask) => ({
          ...subtask,
          sub_task_status:
            subtask.sub_task_status === true || subtask.sub_task_status === 'true' || subtask.sub_task_status === 'completed',
          sub_task_description: subtask.sub_task_description || subtask.description || '',
        }));

        const mappedAssignees = (Array.isArray(item.assign_to) ? item.assign_to : []).map((assignee) => ({
          ...assignee,
          assignee_id: assignee.assignee_id || assignee.firm_user_id || assignee.contact_id,
          assignee_name: assignee.assignee_name || assignee.first_name || assignee.full_name || '',
          first_name: assignee.first_name || assignee.full_name?.split(' ')[0] || '',
          firm_user_id: assignee.firm_user_id,
        }));

        let recentCommentText = '';
        if (typeof item.recent_comment === 'string') {
          recentCommentText = item.recent_comment;
        } else if (item.recent_comment && typeof item.recent_comment === 'object') {
          recentCommentText = item.recent_comment.comment || '';
        }

        return {
          ...item,
          task_title: item.title || item.task_title,
          task_name: item.title || item.task_name,
          task_status: item.status || item.task_status,
          sub_task: mappedSubtasks,
          assign_to: mappedAssignees,
          recent_comment: recentCommentText,
          case_name: item.case_name || '',
          lead_name: item.lead_name || '',
          priority: item.priority || '',
          task_id: item.task_id,
          case_id: item.case_id,
          lead_id: item.lead_id,
          due_date: item.due_date,
          description: item.description,
          created_at: item.created_at,
          updated_at: item.updated_at,
        };
      });

      const currentPage = columnData?.page !== undefined ? columnData.page : pageParam;
      return {
        items: mappedItems,
        page: currentPage,
        hasMore: columnData?.has_more !== undefined ? columnData.has_more : false,
        totalCount: columnData?.task_count || 0,
        task_stage_name: columnData?.task_stage_name,
      };
    };

    // Check if page already exists in cache (only if not forcing refetch)
    if (!forceRefetch) {
      const currentData = queryClient.getQueryData(queryKey);
      if (currentData?.pages) {
        const existingPage = currentData.pages.find((page) => page.page === pageNumber);
        if (existingPage) {
          // Page already in cache, return it
          return Promise.resolve(existingPage);
        }
      }
    }

    // Fetch the specific page
    const pageData = await queryFn({ pageParam: pageNumber });

    // Update the cache with the new page
    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData) {
        return {
          pages: [pageData],
          pageParams: [pageNumber],
        };
      }

      // Check if page already exists
      const pageIndex = oldData.pages.findIndex((p) => p.page === pageNumber);
      if (pageIndex >= 0) {
        // Replace existing page
        const newPages = [...oldData.pages];
        newPages[pageIndex] = pageData;
        return {
          ...oldData,
          pages: newPages,
        };
      } else {
        // Add new page (insert in correct position)
        const newPages = [...oldData.pages, pageData].sort((a, b) => a.page - b.page);
        const newPageParams = [...(oldData.pageParams || []), pageNumber].sort((a, b) => a - b);
        return {
          ...oldData,
          pages: newPages,
          pageParams: newPageParams,
        };
      }
    });

    return pageData;
  };

  return {
    ...infiniteQuery,
    fetchPage,
  };
};
